import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Layers, GraduationCap, BookMarked, Play, Lock, ClipboardList, ChevronRight, Check } from 'lucide-react';
import AssessmentSheet from './AssessmentSheet';
import { hasTest, questionsFor, TEST_LABEL } from '../../../utils/educationAssessments';
import ContentCard from '../../shared/ContentCard';
import ContentViewer from '../../shared/ContentViewer';
import TopicIcon from '../../shared/icons/TopicIcon';
import { SkeletonList } from '../../shared/Skeleton';
import { useBackHandler } from '../../../hooks/NavigationContext';
import { fetchCollectionItems, collectionCover, collectionTopic, kindLabel, COLLECTION_KINDS } from '../../../utils/educationCollections';
import { recordOpen, useEducationLibrary } from '../../../utils/educationLibrary';
import { setCurrentCollection, runFor } from '../../../utils/telemetry';

/*
 * One collection: cover, what it is, its items in order. A series is a shelf to pick from;
 * a curriculum shows its pre-test and post-test slots (locked until assessments ship).
 */

const KIND_ICON = { series: Layers, curriculum: GraduationCap, 'reading-list': BookMarked };

const inlinePdf = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}response-content-disposition=inline` : url);

/** A pre-test or post-test row: open, locked, or taken with the score */
const TestRow = ({ which, col, taken, locked, lockNote, onOpen }) => {
  const n = questionsFor(col, which).length;
  const exists = hasTest(col, which);
  const label = TEST_LABEL[which];
  if (!exists) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 p-3 text-gray-400 dark:text-gray-500">
        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center"><ClipboardList size={18} /></div>
        <div className="flex-1 text-sm font-medium">{label}<span className="block text-xs font-normal">Not written yet</span></div>
      </div>
    );
  }
  if (locked) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-gray-500 dark:text-gray-400">
        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center"><Lock size={18} /></div>
        <div className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-200">{label}<span className="block text-xs font-normal text-gray-500 dark:text-gray-400">{lockNote}</span></div>
      </div>
    );
  }
  const latest = taken && taken.latest;
  return (
    <button onClick={onOpen} className="w-full flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-left shadow-card hover:border-emerald-500 active:scale-[0.99] transition-all">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${latest ? 'bg-emerald-600 text-white' : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'}`}>
        {latest ? <Check size={18} strokeWidth={3} /> : <ClipboardList size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {latest ? `${latest.score} of ${latest.total}${taken.best && taken.best.score !== latest.score ? ` · best ${taken.best.score}` : ''}` : `${n} ${n === 1 ? 'question' : 'questions'}`}
        </div>
      </div>
      <ChevronRight size={18} className="text-gray-400" />
    </button>
  );
};

const EducationCollectionScreen = ({ collection: col, onBack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [test, setTest] = useState(null); // 'pre' | 'post' while a test is open
  const closeViewer = useCallback(() => setSelected(null), []);
  const closeTest = useCallback(() => setTest(null), []);
  useBackHandler(Boolean(selected), closeViewer);
  useBackHandler(Boolean(test), closeTest);

  // Item events opened from here carry the collection and the learner's run
  useEffect(() => {
    setCurrentCollection(col.id);
    if (col.kind === 'curriculum') runFor(col.id);
    return () => setCurrentCollection(null);
  }, [col.id, col.kind]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchCollectionItems(col).then((list) => { if (alive) { setItems(list); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [col]);

  const { completedOf, attemptsFor } = useEducationLibrary();
  const doneCount = items.filter((it) => completedOf(it.id)).length;
  const allDone = !loading && items.length > 0 && doneCount === items.length;
  const taken = attemptsFor(col.id);
  const nextIndex = items.findIndex((it) => !completedOf(it.id));
  const next = nextIndex >= 0 ? items[nextIndex] : null;
  const topic = collectionTopic(col);
  const color = (topic && topic.color) || 'from-emerald-700 to-emerald-900';
  const cover = collectionCover(col, items);
  const KindIcon = KIND_ICON[col.kind] || Layers;
  const kind = COLLECTION_KINDS[col.kind] || COLLECTION_KINDS.series;
  const isCurriculum = col.kind === 'curriculum';

  const open = (item) => {
    if (item.fileType === 'document') {
      recordOpen(item);
      window.open(inlinePdf(item.downloadURL), '_blank', 'noopener,noreferrer');
      return;
    }
    setSelected(item);
  };

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className={`bg-gradient-to-br ${color} text-white px-6 pt-6 pb-5`}>
        <button onClick={() => { onBack(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black/20 shadow-xl">
          {cover ? (
            <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/80">
              <TopicIcon type={topic ? topic.icon : null} className="w-14 h-14" />
            </div>
          )}
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/60 text-[11px] font-semibold text-white">
            <KindIcon size={12} /> {kindLabel(col.kind)}
          </span>
        </div>
        <h1 className="font-merriweather text-2xl font-bold mt-4 leading-tight">{col.title}</h1>
        <p className="text-white/85 text-sm mt-1">
          {items.length || (col.items || []).length} {items.length === 1 ? 'item' : 'items'}
          {topic ? ` · ${topic.label}` : ''}
        </p>
        {col.description && <p className="text-white/90 text-sm mt-3 leading-relaxed">{col.description}</p>}
        {items.length > 0 && next && (
          <button
            onClick={() => open(next)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-gray-900 text-sm font-semibold shadow active:scale-95 transition-transform"
          >
            <Play size={16} className="fill-current" />
            {doneCount === 0 ? `Start with the first ${next.fileType === 'video' ? 'video' : 'item'}` : `Continue with ${nextIndex + 1} of ${items.length}`}
          </button>
        )}
        {items.length > 0 && !next && (
          <p className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/15 text-sm font-semibold">All {items.length} done on this device</p>
        )}
        {!isCurriculum && <p className="text-white/70 text-xs mt-3">{kind.blurb}</p>}
      </div>

      <div className="px-6 py-6 space-y-3">
        {isCurriculum && (col.objectives || []).length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">By the end, you will be able to</div>
            <ol className="space-y-1.5 list-decimal pl-5 text-sm text-gray-800 dark:text-gray-100">
              {col.objectives.map((o, i) => <li key={i}>{o}</li>)}
            </ol>
          </div>
        )}
        {isCurriculum && <TestRow which="pre" col={col} taken={taken.pre} locked={false} onOpen={() => setTest('pre')} />}
        {loading ? (
          <SkeletonList rows={4} />
        ) : items.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-600 dark:text-gray-400">
            Nothing has been added to this {kindLabel(col.kind).toLowerCase()} yet.
          </div>
        ) : (
          items.map((item, i) => <ContentCard key={item.id} item={item} variant="row" index={i + 1} onOpen={open} />)
        )}
        {isCurriculum && (
          <TestRow
            which="post"
            col={col}
            taken={taken.post}
            locked={!allDone}
            lockNote={items.length ? `Finish all ${items.length} items to unlock` : 'Unlocks when the items are done'}
            onOpen={() => setTest('post')}
          />
        )}
      </div>

      {selected && selected.fileType !== 'document' && (
        <ContentViewer content={selected} accent={color} onClose={closeViewer} />
      )}
      {test && <AssessmentSheet collection={col} which={test} accent={color} onClose={closeTest} />}
    </div>
  );
};

export default EducationCollectionScreen;
